import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DataTable, useDataTableContext } from 'components/DataTable'
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { Spinner } from 'react-bootstrap'
import apiService from 'services/api'
import * as S from '../CommonTable.styles'
import { TableSearch } from '../../Filters/TableSearch'
import { getTableQueryKey } from '../../../utils/getTableQueryKey'
import styled from 'styled-components'
import { ColumnsModal } from '../../Modals/ColumnsModal/ColumnsModal'
import { ContactResponse } from 'models/contacts'
import CreateDealModal from '../../Deals/CreateDealModal/CreateDealModal'
import TableButtonAction from 'components/Buttons/TableButtons/TableButtonAction'
import TagAssignModal from 'components/Modals/AssignmentModal/TagAssignModal'
import DistributorAssignModal from 'components/Modals/AssignmentModal/DistributorAssignModal'
import { usePaginationURLParams } from 'utils/usePaginationURLParams'
import { BsBriefcase, BsTags, BsTruck } from 'react-icons/bs'
import {
  universitiesFilterStore,
  useFilterParams,
} from 'components/Filters/FilterStore'
import FilterChips from 'components/Filters/components/FilterChips'
import { UniversitiesFilterset } from './UniversitiesFilterset'
import UniversityExportModal from 'components/Modals/ExportModal/UniversityExportModal'
import { useUniversitiesTableCols } from './universitiesTableColumns'
import OpportunitiesTableFooterControls from 'components/Opportunities/OpportunitiesTableFooterControls'

type SortableFields = (keyof ContactResponse | string)[]

export type ContactsTableRef = {
  refetch: () => void
}

const sortableFields: SortableFields = [
  'institution_name',
  'total_students_enrolled',
  'note_count',
  'contact_count',
  'deal_count',
  'address',
  'city',
  'state_abbr',
  'zipcode',
  'domain',
  'dormitory_capacity',
  'meal_plan_provided',
  'total_enrollment',
  'food_management_company',
  'food_management_company_source',
  'food_management_company_parent',
  'estimated_meals_per_day',
  'carnegie_classification_2021_size_setting_size',
  'carnegie_classification_2021_size_setting_year',
  'carnegie_classification_2021_size_setting_student_composition',
  'sales_stages',
]

const PAGE_SIZE = 100

const UniversitiesTableComponent = forwardRef(
  (
    {
      setTotalRowsCount,
      pageParamName = 'page',
      categoryId,
      subCategoryId,
    }: {
      setTotalRowsCount?: (count: number) => void
      pageParamName?: string
      categoryId?: number
      subCategoryId?: number
    },
    ref
  ) => {
    const tableContext = useDataTableContext()
    const queryClient = useQueryClient()
    const {
      state: { sorting, tableKey, rowSelection, totalSelectedRows },
      methods: { clearSelectedRows, setTotalRowsInBackend },
    } = useDataTableContext()

    const [pagination, setPagination] = usePaginationURLParams(
      PAGE_SIZE,
      pageParamName
    )
    const api = apiService()

    const filterParams = useFilterParams(universitiesFilterStore)

    const sortParams = useMemo(() => {
      const params: Record<string, unknown> = {}

      if (sorting?.length) {
        params['sort'] = sorting[0].desc ? '-' + sorting[0]?.id : sorting[0]?.id
      }

      return params
    }, [sorting])

    const filterAndSortParams = useMemo(
      () => ({
        ...filterParams,
        ...sortParams,
      }),
      [filterParams, sortParams]
    )

    const TABLE_QUERY_KEY = getTableQueryKey({
      tableKey: tableKey,
      filterParams: filterAndSortParams,
      page: pagination.pageIndex + 1,
    })

    // DATA FETCHING
    const { isLoading, data, isFetching, refetch } = useQuery({
      queryKey: TABLE_QUERY_KEY,
      queryFn: async () => {
        await queryClient.cancelQueries(TABLE_QUERY_KEY)
        clearSelectedRows()

        const res = await api.getUniversitiesList({
          page: pagination.pageIndex + 1,
          limit: pagination.pageSize,
          ...filterAndSortParams,
        })
        setTotalRowsCount?.(res.count)
        setTotalRowsInBackend(res.count)
        return res
      },
      keepPreviousData: true,
      staleTime: 1000 * 60 * 5,
    })
    // END DATA FETCHING

    useEffect(() => {
      clearSelectedRows()
    }, [data])

    const [showAssignTags, setShowAssignTags] = useState(false)
    const [showAssignDistributors, setShowAssignDistributors] = useState(false)
    const [openDealModal, setOpenDealModal] = React.useState(false)
    const [dealCompanuIds, setDealCompanyIds] = React.useState<number[]>([])
    const [showExportAllModal, setShowExportAllModal] = useState(false)
    const handleCreateDeals = () => {
      if (!data) return
      const companyIds = Object.keys(rowSelection).map(
        (idx) => data.results[parseInt(idx)].id
      )
      setDealCompanyIds(companyIds)
      setOpenDealModal(true)
    }
    // Make refetch available to parent
    useImperativeHandle(
      ref,
      () =>
        ({
          refetch: () => refetch(),
        }) as ContactsTableRef
    )

    const columns = useUniversitiesTableCols({
      sorting,
    })

    useEffect(() => {
      setTotalRowsCount?.(data?.count ?? 0)
    }, [data?.count])

    return (
      <TableContainer>
        <S.SearchContainer>
          <S.SearchContainerFilters>
            <TableSearch
              filterStore={universitiesFilterStore}
              searchPlaceholder="Search universities by name"
              className="flex-1"
            />
            <UniversitiesFilterset />
            <ColumnsModal />
            <TableButtonAction
              items={[
                {
                  label: 'Create Deals',
                  icon: <BsBriefcase size={20} />,
                  callback: handleCreateDeals,
                },
                {
                  label: 'Manage Tags',
                  icon: <BsTags size={20} />,
                  callback: () => setShowAssignTags(true),
                },
                {
                  label: 'Add Known Distributors',
                  icon: <BsTruck size={20} />,
                  callback: () => setShowAssignDistributors(true),
                },
              ]}
              disabled={!totalSelectedRows}
              selectedRowsCount={totalSelectedRows ?? 0}
            />
          </S.SearchContainerFilters>
          <FilterChips
            clearAllButton
            showActive
            store={universitiesFilterStore}
          />
        </S.SearchContainer>

        <TagAssignModal
          show={showAssignTags}
          handleClose={() => setShowAssignTags(false)}
          tableQueryKey={TABLE_QUERY_KEY}
          data={data?.results ?? []}
          filterAndSortParams={filterAndSortParams}
          apiTagsAction={api.universitiesBulkEditTags}
          idAccessor="id"
        />
        <DistributorAssignModal
          show={showAssignDistributors}
          handleClose={() => setShowAssignDistributors(false)}
          tableQueryKey={TABLE_QUERY_KEY}
          data={data?.results ?? []}
          idAccessor="id"
          filterAndSortParams={filterAndSortParams}
          apiDistributorsAction={api.universitiesBulkEditDistributors}
        />

        <UniversityExportModal
          count={data?.count ?? 0}
          show={showExportAllModal}
          _handleClose={() => {
            setShowExportAllModal(false)
          }}
          filters={{
            ...filterAndSortParams,
          }}
        />

        <DataTable
          key={tableContext.state.tableKey}
          autoLayout
          data={data?.results ?? []}
          columns={columns}
          sortableFields={sortableFields}
          virtualizeRows={true}
          enableRowSelection
          isPaginationEnabled={true}
          paginationOptions={{
            pageCount: Math.ceil((data?.count ?? 0) / PAGE_SIZE),
            setPagination: setPagination,
            pagination: pagination,
            isPaginationLoading: isLoading,
          }}
        >
          <DataTable.FooterControls>
            <OpportunitiesTableFooterControls
              categoryId={categoryId}
              subCategoryId={subCategoryId}
              onDownloadButtonClick={() => setShowExportAllModal(true)}
            />
          </DataTable.FooterControls>
        </DataTable>

        <LoadingOverlay show={isFetching}>
          <Spinner />
        </LoadingOverlay>

        {openDealModal && (
          <CreateDealModal
            origin="companies"
            isUserCompany={true}
            companyIds={dealCompanuIds}
            show={openDealModal}
            handleClose={() => setOpenDealModal(false)}
            onDealCreated={refetch}
            queryKey={TABLE_QUERY_KEY}
            filterAndSortParams={filterAndSortParams}
          />
        )}
      </TableContainer>
    )
  }
)
UniversitiesTableComponent.displayName = 'UniversitiesTable'

export const UniversitiesTable = UniversitiesTableComponent

const TableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow: hidden;
`

const LoadingOverlay = styled.div<{
  show: boolean
}>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1px);
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 30;
`
